import React, { useState } from 'react';
import { FaEdit, FaEye, FaTrash, FaPlus, FaSearch, FaFilter } from 'react-icons/fa';
import AdminLayout from '../components/admin/AdminLayout';

const AdminPagesList: React.FC = () => {
  const [language, setLanguage] = useState<'ar' | 'en'>('ar');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const isRTL = language === 'ar';

  const pages = [
    { 
      id: 'home', 
      title: isRTL ? 'الصفحة الرئيسية' : 'Home Page', 
      slug: 'home',
      status: 'active', 
      lastModified: '2024-01-15',
      sections: 6,
      views: 1234
    },
    { 
      id: 'about', 
      title: isRTL ? 'من نحن' : 'About Us', 
      slug: 'about',
      status: 'active', 
      lastModified: '2024-01-14',
      sections: 8,
      views: 856
    },
    { 
      id: 'services', 
      title: isRTL ? 'الخدمات' : 'Services', 
      slug: 'services',
      status: 'active', 
      lastModified: '2024-01-13',
      sections: 4,
      views: 654
    },
    { 
      id: 'projects', 
      title: isRTL ? 'المشاريع' : 'Projects', 
      slug: 'projects',
      status: 'active', 
      lastModified: '2024-01-12',
      sections: 5,
      views: 432
    },
    { 
      id: 'contact', 
      title: isRTL ? 'اتصل بنا' : 'Contact', 
      slug: 'contact',
      status: 'active', 
      lastModified: '2024-01-11',
      sections: 3,
      views: 321
    },
    { 
      id: 'join-us', 
      title: isRTL ? 'انضم إلينا' : 'Join Us', 
      slug: 'join-us',
      status: 'draft', 
      lastModified: '2024-01-10',
      sections: 2,
      views: 0
    }
  ];

  const filteredPages = pages.filter(page => {
    const matchesSearch = page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         page.slug.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || page.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const handleDelete = (pageId: string) => {
    if (window.confirm(isRTL ? 'هل أنت متأكد من حذف هذه الصفحة؟' : 'Are you sure you want to delete this page?')) {
      console.log('Deleting page:', pageId);
      // TODO: Implement delete functionality
    }
  };

  return (
    <AdminLayout language={language} currentPage="pages">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isRTL ? 'إدارة الصفحات' : 'Pages Management'}
            </h1>
            <p className="text-gray-600">
              {isRTL ? 'إدارة جميع صفحات الموقع' : 'Manage all website pages'}
            </p>
          </div>
          <div className="flex gap-2">
            <button 
              className={`px-3 py-1 rounded ${language === 'ar' ? 'bg-blue-700 text-white' : 'bg-gray-200'}`} 
              onClick={() => setLanguage('ar')}
            >
              عربي
            </button>
            <button 
              className={`px-3 py-1 rounded ${language === 'en' ? 'bg-blue-700 text-white' : 'bg-gray-200'}`} 
              onClick={() => setLanguage('en')}
            >
              EN
            </button>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col md:flex-row gap-4 flex-1">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={isRTL ? 'البحث في الصفحات...' : 'Search pages...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Filter */}
              <div className="flex items-center gap-2">
                <FaFilter className="text-gray-400" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">{isRTL ? 'جميع الحالات' : 'All Status'}</option>
                  <option value="active">{isRTL ? 'نشط' : 'Active'}</option>
                  <option value="draft">{isRTL ? 'مسودة' : 'Draft'}</option>
                  <option value="archived">{isRTL ? 'مؤرشف' : 'Archived'}</option>
                </select>
              </div>
            </div>

            {/* Add New Page */}
            <a
              href="/admin/pages/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <FaPlus className="w-4 h-4" />
              {isRTL ? 'إضافة صفحة جديدة' : 'Add New Page'}
            </a>
          </div>
        </div>

        {/* Pages Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {isRTL ? 'الصفحة' : 'Page'}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {isRTL ? 'الحالة' : 'Status'}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {isRTL ? 'السكاشن' : 'Sections'}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {isRTL ? 'المشاهدات' : 'Views'}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {isRTL ? 'آخر تعديل' : 'Last Modified'}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {isRTL ? 'الإجراءات' : 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPages.map((page) => (
                  <tr key={page.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{page.title}</div>
                        <div className="text-sm text-gray-500">/{page.slug}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        page.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : page.status === 'draft'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {page.status === 'active' ? (isRTL ? 'نشط' : 'Active') : 
                         page.status === 'draft' ? (isRTL ? 'مسودة' : 'Draft') : 
                         (isRTL ? 'مؤرشف' : 'Archived')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {page.sections}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {page.views.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {page.lastModified}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <a
                          href={`/admin/pages/${page.id}/builder`}
                          className="text-blue-600 hover:text-blue-900"
                          title={isRTL ? 'تحرير' : 'Edit'}
                        >
                          <FaEdit className="w-4 h-4" />
                        </a>
                        <a
                          href={`/${page.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-900"
                          title={isRTL ? 'معاينة' : 'Preview'}
                        >
                          <FaEye className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => handleDelete(page.id)}
                          className="text-red-600 hover:text-red-900"
                          title={isRTL ? 'حذف' : 'Delete'}
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredPages.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {isRTL ? 'لا توجد صفحات تطابق البحث' : 'No pages match your search'}
              </p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPagesList; 